import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc,
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { uploadSignature } from '@/services/esignService';

export interface AgreementCompletion {
  id?: string;
  agreementId: string;
  agreementName: string;
  signerName: string;
  signerEmail?: string;
  signatureDataUrl: string;
  signatureUrl?: string;
  signedPdfUrl?: string; // URL to the PDF with signature embedded
  signedAt: any;
  clientSignedAt?: number; // Client-side timestamp (milliseconds) for immediate display
  organizationId?: string;
  locationId?: string;
  owner_role?: string; // Owner role from compliance template for access control
  vendorId?: string; // Optional: associated vendor (for vendor contracts)
  vendorName?: string; // Vendor name for display (denormalized)
  employeeId?: string; // Optional: associated employee (for employee contracts)
  employeeName?: string; // Employee name for display (denormalized)
  createdAt?: any;
  // AI-parsed contract information
  contractSummary?: string;
  contractDate?: any; // Timestamp or ISO string
  expiryDate?: any; // Timestamp or ISO string
  contractKeyInformation?: Record<string, string>; // Key-value pairs of extracted information
  fieldValues?: Record<string, any>; // Custom field values (fieldName -> value)
  status?: 'Active' | 'Expired' | 'Pending Renewal' | 'Terminated'; // Contract status
  startDate?: any; // Contract start date (Timestamp or ISO string)
  endDate?: any; // Contract end date (Timestamp or ISO string)
  autoRenew?: boolean; // Whether contract auto-renews
  renewalPeriod?: string; // Renewal period (e.g., "1 year", "6 months")
  pendingRenewalDays?: number; // Days before expiry to mark as "Pending Renewal" (default: 30)
}

export const agreementCompletionService = {
  // Add an agreement completion record
  addCompletion: async (completionData: Omit<AgreementCompletion, 'id' | 'signedAt' | 'createdAt'> & { signedDate?: Date }) => {
    try {
      // Fetch owner_role from the agreement document's compliance template
      let ownerRole: string | undefined = completionData.owner_role;
      if (!ownerRole && completionData.agreementId) {
        try {
          const { agreementService } = await import('./agreementService');
          const agreementResult = await agreementService.getDocument(completionData.agreementId);
          if (agreementResult.success && agreementResult.data) {
            // Use owner_role from agreement document if available
            ownerRole = agreementResult.data.owner_role;
            
            // If not available, fetch from compliance template
            if (!ownerRole && agreementResult.data.templateId) {
              const { complianceService } = await import('./complianceService');
              const templateResult = await complianceService.getTemplate(agreementResult.data.templateId);
              if (templateResult.success && templateResult.data) {
                ownerRole = templateResult.data.owner_role;
              }
            }
          }
        } catch (error) {
          console.warn('Error fetching agreement document for owner_role:', error);
          // Continue without owner_role if fetch fails
        }
      }
      
      // Try to upload signature to Firebase Storage
      let signatureUrl: string | undefined;
      try {
        if (completionData.signatureDataUrl) {
          signatureUrl = await uploadSignature(
            completionData.signatureDataUrl,
            `agreement_signature_${completionData.agreementId}_${Date.now()}.png`,
            {
              docType: 'agreement_signature',
              organizationId: completionData.organizationId,
              locationId: completionData.locationId,
              agreementId: completionData.agreementId,
              uploadedBy: completionData.signerEmail || completionData.signerName
                ? {
                    email: completionData.signerEmail,
                    name: completionData.signerName,
                  }
                : undefined,
              title: `${completionData.agreementName} - Signature`,
              source: 'web',
              additionalMetadata: {
                signerName: completionData.signerName,
              },
            }
          );
        }
      } catch (signatureError) {
        console.warn('⚠️ Signature upload failed, storing as data URL:', signatureError);
        // Continue with data URL if upload fails
      }

      // Use serverTimestamp, but also store the client date as a number (milliseconds since epoch)
      // for immediate display before Firestore resolves the timestamp
      const clientTimestamp = completionData.signedDate?.getTime() || Date.now();
      
      const { signedDate, ...dataWithoutDate } = completionData;
      
      const completion: Omit<AgreementCompletion, 'id'> & { clientSignedAt?: number } = {
        ...dataWithoutDate,
        ...(ownerRole !== undefined && { owner_role: ownerRole }),
        signatureUrl,
        signedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        clientSignedAt: clientTimestamp, // Store client-side timestamp for immediate use
      };

      // Remove undefined values
      const cleanedCompletion = removeUndefined(completion);

      const docRef = await addDoc(collection(db, 'agreementCompletions'), cleanedCompletion);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding agreement completion:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all completions for a specific agreement
  getCompletionsForAgreement: async (agreementId: string) => {
    try {
      // Fetch without orderBy to avoid composite index requirement, then sort in memory
      const q = query(
        collection(db, 'agreementCompletions'),
        where('agreementId', '==', agreementId)
      );
      const querySnapshot = await getDocs(q);
      const completions: AgreementCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as AgreementCompletion);
      });
      
      // Sort by signedAt in memory (descending - newest first)
      completions.sort((a, b) => {
        const aDate = a.signedAt?.toDate ? a.signedAt.toDate().getTime() : 
                     (a.signedAt instanceof Date ? a.signedAt.getTime() : 
                     (typeof a.signedAt === 'number' ? a.signedAt : 0));
        const bDate = b.signedAt?.toDate ? b.signedAt.toDate().getTime() : 
                     (b.signedAt instanceof Date ? b.signedAt.getTime() : 
                     (typeof b.signedAt === 'number' ? b.signedAt : 0));
        return bDate - aDate; // Descending order (newest first)
      });
      
      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting agreement completions:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  // Get all completions for an organization
  getCompletionsForOrganization: async (organizationId: string) => {
    try {
      // Fetch without orderBy to avoid composite index requirement, then sort in memory
      const q = query(
        collection(db, 'agreementCompletions'),
        where('organizationId', '==', organizationId)
      );
      const querySnapshot = await getDocs(q);
      const completions: AgreementCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as AgreementCompletion);
      });
      
      // Sort by signedAt in memory (descending - newest first)
      completions.sort((a, b) => {
        const aDate = a.signedAt?.toDate ? a.signedAt.toDate().getTime() : 
                     (a.signedAt instanceof Date ? a.signedAt.getTime() : 
                     (typeof a.signedAt === 'number' ? a.signedAt : 0));
        const bDate = b.signedAt?.toDate ? b.signedAt.toDate().getTime() : 
                     (b.signedAt instanceof Date ? b.signedAt.getTime() : 
                     (typeof b.signedAt === 'number' ? b.signedAt : 0));
        return bDate - aDate; // Descending order (newest first)
      });
      
      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting organization agreement completions:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  // Get all completions for a specific vendor
  getCompletionsForVendor: async (vendorId: string, organizationId?: string, vendorName?: string) => {
    try {
      const completions: AgreementCompletion[] = [];
      
      // First, try to get by vendorId
      let q;
      if (organizationId) {
        q = query(
          collection(db, 'agreementCompletions'),
          where('vendorId', '==', vendorId),
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(
          collection(db, 'agreementCompletions'),
          where('vendorId', '==', vendorId)
        );
      }
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as AgreementCompletion);
      });
      
      // If no results and vendorName is provided, also search by vendorName
      // This handles cases where older contracts might only have vendorName set
      if (completions.length === 0 && vendorName) {
        let qByName;
        if (organizationId) {
          qByName = query(
            collection(db, 'agreementCompletions'),
            where('vendorName', '==', vendorName),
            where('organizationId', '==', organizationId)
          );
        } else {
          qByName = query(
            collection(db, 'agreementCompletions'),
            where('vendorName', '==', vendorName)
          );
        }
        const nameQuerySnapshot = await getDocs(qByName);
        nameQuerySnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() } as AgreementCompletion;
          // Only add if not already in completions (avoid duplicates)
          if (!completions.find(c => c.id === data.id)) {
            completions.push(data);
          }
        });
      }
      
      console.log(`[getCompletionsForVendor] Found ${completions.length} contracts for vendorId: ${vendorId}, vendorName: ${vendorName || 'N/A'}`);
      
      // Sort by signedAt in memory (descending - newest first)
      completions.sort((a, b) => {
        const aDate = a.signedAt?.toDate ? a.signedAt.toDate().getTime() : 
                     (a.signedAt instanceof Date ? a.signedAt.getTime() : 
                     (typeof a.signedAt === 'number' ? a.signedAt : 0));
        const bDate = b.signedAt?.toDate ? b.signedAt.toDate().getTime() : 
                     (b.signedAt instanceof Date ? b.signedAt.getTime() : 
                     (typeof b.signedAt === 'number' ? b.signedAt : 0));
        return bDate - aDate; // Descending order (newest first)
      });
      
      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting vendor agreement completions:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  // Update expiry date for a completion
  updateExpiryDate: async (completionId: string, expiryDate: Date | string) => {
    try {
      const expiryDateString = expiryDate instanceof Date ? expiryDate.toISOString() : expiryDate;
      const completionRef = doc(db, 'agreementCompletions', completionId);
      await updateDoc(completionRef, {
        expiryDate: expiryDateString,
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error updating expiry date:', error);
      return { success: false, error: error.message };
    }
  },
};

// Helper function to remove undefined values from objects
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

